import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import LocalizedLink from '../i18n/LocalizedLink';
import { usePageTitle } from '../hooks/usePageTitle';
import { SEOMeta } from '../templates/SEOMeta';
import AdSense from './AdSense';
import InfoModal from './InfoModal';
import RecommendedSection from './RecommendedSection';

const HomePage: React.FC = () => {
  const { t } = useTranslation();
  usePageTitle(t('home.pageTitle'));
  
  const [isOnlineModalOpen, setIsOnlineModalOpen] = useState(false);
  const [isTableModalOpen, setIsTableModalOpen] = useState(false);

  const onlineSteps = t('home.online.modal.steps', { returnObjects: true }) as string[];
  const tableSteps = t('home.table.modal.steps', { returnObjects: true }) as string[];
  
  return (
    <>
      <SEOMeta
        title={t('home.seo.title')}
        description={t('home.seo.description')}
        canonical="https://el-impostor.com/"
        keywords={t('home.seo.keywords')}
      />
      <div className="min-h-screen md:flex md:items-center md:justify-center p-4">
      <div className="max-w-md w-full md:max-w-md">
        {/* Anuncio superior */}
        {/* 
        <div className="mb-6">
          <AdSense 
            slot="1234567890"
            format="auto"
            style={{ display: 'block', textAlign: 'center' }}
          />
        </div>
        */}

        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-primary-800 mb-2 text-shadow">
            🎭 {t('home.brand')}
          </h1>
          <p className="text-gray-600 dark:text-gray-300 text-lg">
            {t('home.hero.tagline')}
          </p>
        </div>

        <RecommendedSection position="top" />

        <div className="card space-y-6">
          {/* Sección Multijugador Online */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-bold text-primary-800">{t('home.online.title')}</h2>
              <button
                onClick={() => setIsOnlineModalOpen(true)}
                className="w-8 h-8 rounded-full bg-primary-100 hover:bg-primary-200 text-primary-700 flex items-center justify-center transition-colors"
                aria-label={t('home.online.moreInfoAria')}
                title={t('home.moreInfoTitle')}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
              {t('home.online.description')}
            </p>
            <div className="space-y-2">
              <LocalizedLink to="/create" className="block">
                <button className="btn-primary w-full text-lg py-3.5">
                  {t('home.online.createRoom')}
                </button>
              </LocalizedLink>
              
              <LocalizedLink to="/join" className="block">
                <button className="btn-secondary w-full text-lg py-3.5">
                  {t('home.online.joinRoom')}
                </button>
              </LocalizedLink>
            </div>
          </div>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300 dark:border-gray-800"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-blue-50 dark:bg-gray-950 text-gray-500 dark:text-gray-400 font-medium">{t('home.divider.or')}</span>
            </div>
          </div>

          {/* Sección Modo Mesa */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-bold bg-gradient-to-r from-purple-700 to-pink-700 bg-clip-text text-transparent">
                {t('home.table.title')}
              </h2>
              <button
                onClick={() => setIsTableModalOpen(true)}
                className="w-8 h-8 rounded-full bg-purple-100 hover:bg-purple-200 dark:bg-purple-900/40 dark:hover:bg-purple-900/60 text-purple-700 dark:text-purple-200 flex items-center justify-center transition-colors"
                aria-label={t('home.table.moreInfoAria')}
                title={t('home.moreInfoTitle')}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
              {t('home.table.description')}
            </p>
            <LocalizedLink to="/table" className="block">
              <button className="w-full text-lg py-3.5 bg-gradient-to-r from-purple-600 to-pink-600 
                               hover:from-purple-700 hover:to-pink-700 text-white font-semibold 
                               rounded-lg shadow-md hover:shadow-lg transition-all duration-200 
                               transform hover:scale-105">
                {t('home.table.play')}
              </button>
            </LocalizedLink>
          </div>

          {/* Sección de Enlaces */}
          <div className="pt-4 border-t border-gray-200 dark:border-gray-800">
            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              <LocalizedLink 
                to="/contact" 
                className="inline-flex items-center justify-center gap-2 text-primary-600 hover:text-primary-700 font-medium px-4 py-2 rounded-lg hover:bg-primary-50 dark:hover:bg-gray-800 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                {t('home.links.contact')}
              </LocalizedLink>
            </div>


            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              <LocalizedLink 
                to="/about" 
                className="inline-flex items-center justify-center gap-2 text-primary-600 hover:text-primary-700 font-medium px-4 py-2 rounded-lg hover:bg-primary-50 dark:hover:bg-gray-800 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {t('home.links.about')}
              </LocalizedLink>
              <LocalizedLink 
                to="/blog" 
                className="inline-flex items-center justify-center gap-2 text-primary-600 hover:text-primary-700 font-medium px-4 py-2 rounded-lg hover:bg-primary-50 dark:hover:bg-gray-800 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                {t('home.links.guide')}
              </LocalizedLink>
            </div>
          </div>
        </div>

        <RecommendedSection position="bottom" />

        {/* Modals */}
        <InfoModal
          isOpen={isOnlineModalOpen}
          onClose={() => setIsOnlineModalOpen(false)}
          title={t('home.online.modal.title')}
          description={t('home.online.modal.description')}
          steps={onlineSteps}
          blogUrl="/blog/modalidad/multijugador"
          emoji="🌐"
        />

        <InfoModal
          isOpen={isTableModalOpen}
          onClose={() => setIsTableModalOpen(false)}
          title={t('home.table.modal.title')}
          description={t('home.table.modal.description')}
          steps={tableSteps}
          blogUrl="/blog/modalidad/mesa"
          emoji="🎲"
        />
        
        {/* Anuncio inferior */}
        {/* 
        <div className="mt-6">
          <AdSense 
            slot="0987654321"
            format="auto"
            style={{ display: 'block', textAlign: 'center' }}
          />
        </div>
        */}

        <div className="text-center mt-6">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t('home.footer.finePrint')}
          </p>
        </div>
        {/* Carrusel de Juegos 
        <div className="mt-6">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">{t('home.footer.recommended')}</h3>
          <GameCarousel 
            games={gamesConfig} 
            speed={10}
            pauseOnHover={true}
          />
        </div>
        */}

      </div>
    </div>
    </>
  );
};

export default HomePage;
